import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useRecaptcha } from "@/hooks/useRecaptcha";

export default function Login() {
  useDocumentTitle("Acceso");
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const recaptcha = useRecaptcha();

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    // V1.37 · Obtén token reCAPTCHA v3 (vacío si no está activo o falla)
    const recaptchaToken = await recaptcha.execute("login");
    const res = await login(email.trim(), password, { recaptchaToken });
    setSubmitting(false);
    if (res.ok) {
      toast.success("Sesión iniciada");
      navigate("/", { replace: true });
    } else {
      toast.error(res.error || "Error al iniciar sesión");
      // V1.37.3 · resetea el widget v2 tras fallo para que el usuario reintente
      recaptcha.reset?.();
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-12 sm:px-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <Logo variant="hero" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Panel del profesor</h1>
          <p className="text-neutral-500 mb-8">Inicia sesión para gestionar alumnos y citas.</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">User data</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 bg-white px-4 h-12 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="xxxxxxx"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Authorization Code</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 bg-white px-4 h-12 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder=""
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={
                submitting ||
                // V1.37.3 · Solo deshabilitamos si v2 está activo Y el widget
                // se cargó bien (sin errores) Y aún no hay token.
                // Si el widget falló al cargar (loadError), permitimos submit:
                // el backend hará fail-open (política ultra-safe).
                (recaptcha.enabled && recaptcha.version === "v2"
                  && recaptcha.ready && !recaptcha.loadError && !recaptcha.v2Token)
              }
              className="btn-primary w-full h-12 text-base"
            >
              <LogIn className="h-5 w-5" />
              {submitting ? "Entrando…" : "Entrar"}
            </button>
            {recaptcha.enabled && recaptcha.version === "v2" && (
              <div className="flex justify-center" data-testid="login-recaptcha-v2-widget">
                <div ref={recaptcha.v2ContainerRef} />
              </div>
            )}
            {recaptcha.enabled && (
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-400"
                   data-testid="login-recaptcha-badge">
                <ShieldCheck className="h-3 w-3 text-emerald-600" />
                Protegido por Google reCAPTCHA {recaptcha.version === "v3" ? "v3" : "v2"}
              </div>
            )}
          </form>

          <p className="mt-10 text-xs text-neutral-400">© Natur Autoescuela · Panel privado del profesor</p>
        </div>
      </div>

      {/* Image side */}
      <div className="hidden lg:flex relative items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-white p-12 overflow-hidden">
        <img
          src="/natur-logo-login.png"
          alt="Coche oficial de Natur Autoescuela"
          className="w-full max-w-2xl object-contain drop-shadow-xl"
          draggable={false}
        />
        <div className="absolute bottom-10 left-12 right-12 text-neutral-700">
          <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-2">
            Gestión diaria
          </div>
          <div className="text-2xl xl:text-3xl font-bold leading-tight max-w-md text-neutral-900">
            Alumnos, citas y clases en un solo lugar.
          </div>
        </div>
      </div>
    </div>
  );
}
